<template>
  <main-section
    :isEditMode="isEditMode"
    :mainStyle="!isEditMode ? { 'margin-top': '10px' } : {}"
    :isDiff="app.isDiff"
    :isOutdated="isOutdated"
    :itemType="'APPLICATION'"
    :typename="'application'"
    :item="app"
    :onUpdate="updateApp"
    :onCreate="createApp"
    :onDelete="deleteApp"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    :isLoading="isLoading"
    :isEdited="isAppEdited"
  >
    <application-menu slot="top-menu" :app="app" v-if="mode === 'edit'"></application-menu>
    <div slot="fieldset" :disabled="isOutdated" ref="droptarget">
      <!-- Type -->
      <div class="required">
        <div id="typeMessage" class="ui icon message" v-if="appType" :class="{ info: !tempType }">
          <i class="icon" :class="appType.icon" :style="{ color: appType.color }"></i>
          <div class="content">
            <div class="header">
              {{ $t(appType.name || appType.type) }}
            </div>
            <div
              class="description"
              v-html="$t(appType.description || appType.type + '-DESCRIPTION')"
            ></div>
          </div>
        </div>
        <typeahead
          id="typeChooser"
          :allowSearch="false"
          :required="true"
          :items="selectableAppTypes"
          :max-items="100"
          :labelText="$t('SWITCH', { thing: $tc('TYPE', 1) })"
          :current-item="app.type"
          :placeholder-text="$tc('TYPE', 1)"
          :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('TYPE', 2) })"
          :select-handler="appTypeSelected"
          @hover="updateTemporaryTypeDescription"
          v-diff-field="appDiff.type"
          :disabled="app.isDiff"
        >
          <template v-slot:dropdown="props">
            <i
              v-if="appTypeByName[props.item.type]"
              class="icon"
              :class="appTypeByName[props.item.type].icon"
              :style="{ color: appTypeByName[props.item.type].color }"
            />
            {{ $t(props.item.name) }}
          </template>

          <template v-slot:selected="props">
            <i
              v-if="appTypeByName[props.item.name]"
              class="icon"
              :class="appTypeByName[props.item.name].icon"
              :style="{ color: appTypeByName[props.item.name].color }"
            />
            {{ $t(appTypeByName[props.item.name] && appTypeByName[props.item.name].name) }}
          </template>
        </typeahead>
      </div>

      <div class="field">
        <div class="two fields">
          <div
            class="twelve wide field required"
            :class="{ error: $v.app.name.$error, info: copied && !$v.app.name.$error }"
            v-diff-field.disable="appDiff.name"
          >
            <!-- Name -->
            <label for="appName">{{ $t('NAME') }}</label>
            <input
              id="appName"
              type="text"
              :placeholder="$t('NAME')"
              v-model.trim="app.name"
              @click="$v.app.name.$touch()"
              ref="nameInput"
            />
          </div>

          <!-- Version -->
          <div class="four wide field" v-diff-field.disable="appDiff.appVersion">
            <label for="appVersion">{{ $tc('VERSION') }}</label>
            <input
              id="appVersion"
              type="text"
              :placeholder="$tc('VERSION')"
              v-model.trim="app.appVersion"
            />
          </div>
        </div>

        <div class="two fields">
          <!-- ID -->
          <div class="twelve wide field" v-if="app.id">
            <label for="appIdentifier">{{ $t('IDENTIFIER') }}</label>
            <input
              id="appIdentifier"
              type="text"
              :placeholder="$t('IDENTIFIER')"
              v-model.trim="app.id"
              disabled="disabled"
            />
          </div>

          <!-- Manufacturer -->
          <div class="four wide field" v-diff-field.disable="appDiff.manufacturer">
            <typeahead
              :items="manufacturers"
              :current-item="appManufacturer.name"
              :label-text="$tc('MANUFACTURER', 1) | capitalize"
              :placeholder-text="$tc('MANUFACTURER', 1) | capitalize"
              :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('MANUFACTURER', 2) })"
              :select-handler="manufacturerSelected"
              :create-handler="manufacturerCreated"
              :remove-handler="manufacturerRemoved"
            />
          </div>
        </div>

        <!-- Groups -->
        <group-typeahead
          :allGroups="groups"
          :currentGroups="editedGroups"
          :disabled="app.isDiff || !groups"
          @selected="groupSelected"
          @created="groupCreated"
          @removed="groupRemoved"
        />

        <div class="fields">
          <!-- Flag: Hide from user -->
          <div class="field" v-diff-field.disable="appDiff.hideFromUser">
            <div class="ui toggle checkbox">
              <input id="hideFromUser" type="checkbox" v-model="app.hideFromUser" />
              <label for="hideFromUser">
                {{ $t('HIDE_FROM_USER', { thing: $tc('APPLICATION', 1).toLowerCase() }) }}
              </label>
            </div>
          </div>

          <!-- Flag: App is embedded in another app -->
          <div
            class="field"
            v-diff-field.disable="appDiff.isEmbedded"
            v-if="app.type === 'JAVA-AWT'"
          >
            <div class="ui toggle checkbox">
              <input id="isEmbedded" type="checkbox" v-model="app.isEmbedded" />
              <label for="isEmbedded">
                {{ $t('APPLICATION_IS_EMBEDDED') }}
              </label>
            </div>
          </div>

          <!-- Flag: App should use COM interface -->
          <div class="field" v-diff-field.disable="appDiff.useCom" v-if="app.type === 'WEB-HIE'">
            <div class="ui toggle checkbox">
              <input id="useCom" type="checkbox" v-model="app.useCom" />
              <label for="useCom">
                {{ $t('APPLICATION_LEGACY_BROWSER') }}
              </label>
            </div>
          </div>

          <!-- Flag: Allow embedded chrome camera/microphone -->
          <div
            class="field"
            v-diff-field.disable="appDiff.switches"
            v-if="app.type === 'WEB-HCHROME'"
          >
            <div class="ui toggle checkbox">
              <input id="allowHChromeMedia" type="checkbox" v-model="allowHChromeMedia" />
              <label for="allowHChromeMedia">
                {{ $t('ALLOW_CAMERA_MICROPHONE') }}
              </label>
            </div>
          </div>
        </div>

        <div class="fields">
          <!-- Flag: Popups open in IE or embedded widnows -->
          <div class="field" v-if="app.type === 'WEB-HIE'">
            <label>{{ $t('POPUP_HANDLING') }}</label>
            <div class="fields" v-diff-field.disable="appDiff.popupHandling">
              <div class="field" :title="$t('POPUP_HELP_IN_BROWSER')">
                <div class="ui radio checkbox">
                  <input
                    type="radio"
                    class="hidden"
                    id="application-popup-browser"
                    value="PopupShowInBrowser"
                    v-model="app.popupHandling"
                    name="application-popup"
                  />
                  <label for="application-popup-browser">{{
                    $t('OPEN_POPUP', { in: $t('BROWSER') })
                  }}</label>
                </div>
              </div>
              <div class="field" :title="$t('POPUP_HELP_IN_INTERNALWINDOW')">
                <div class="ui radio checkbox">
                  <input
                    type="radio"
                    class="hidden"
                    id="application-popup-internal-window"
                    value="PopupShowInInternalWindow"
                    v-model="app.popupHandling"
                    name="application-popup"
                  />
                  <label for="application-popup-internal-window">{{
                    $t('OPEN_POPUP', { in: $t('INTERNAL_WINDOW') })
                  }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Launch section -->
      <div class="ui top attached secondary tabular pointing menu" v-if="isNotHeadlessApp">
        <div
          class="item"
          :class="{ active: !showLaunchWithFlow }"
          @click="showLaunchWithFlow = false"
        >
          {{ $t('LAUNCH_SECTION') }}
          &nbsp;
          <i class="ui green check circle icon" v-if="!app.launchWithFlowId" />
        </div>
        <div
          class="item"
          :class="{ active: showLaunchWithFlow }"
          @click="showLaunchWithFlow = true"
        >
          {{ $t('LAUNCH_SECTION_FLOW') }}
          &nbsp;
          <i class="ui green check circle icon" v-if="app.launchWithFlowId" />
        </div>
      </div>
      <div class="ui basic segment" v-if="isNotHeadlessApp">
        <template v-if="showLaunchWithFlow">
          <!-- Combo box for launch with all flows for app -->
          <div class="field" v-diff-field.disable="appDiff.launchWithFlowId">
            <label>{{ $t('CHOOSE', { thing: $tc('FLOW', 1) }) }}</label>
            <typeahead
              :items="flowsOnApp"
              :current-item="launchWithFlowName"
              :placeholder-text="$tc('FLOW', 1)"
              :empty-text="$t('NO_SELECTION')"
              @selected="selectLaunchWithFlow"
            />
          </div>
        </template>
        <template v-else>
          <div class="fields">
            <!-- Launch with -->
            <div class="six wide field" v-diff-field.disable="appDiff.launchwith">
              <label for="appLaunchWith">{{ $t('LAUNCHWITH') }}</label>
              <input
                id="appLaunchWith"
                type="text"
                :placeholder="$t('LAUNCHWITH')"
                v-model.trim="app.launchwith"
              />
            </div>

            <div
              class="four wide field"
              v-diff-field.disable="appDiff.jvmlocation"
              v-if="app.type === 'JAVA-AWT'"
            >
              <label for="jvmLocation">{{ $t('JVMLOCATION') }}</label>
              <input
                id="jvmLocation"
                type="text"
                :placeholder="$t('JVMLOCATION')"
                v-model.trim="app.jvmlocation"
              />
            </div>

            <!-- Launch in (tab/window/incognito/app) -->
            <div class="field" v-if="isTabbedBrowserApplication">
              <label>{{ $t('OPEN_PAGE_IN') }}</label>
              <div class="fields" v-diff-field.disable="appDiff.openIn">
                <div class="field">
                  <div class="ui radio checkbox">
                    <input
                      type="radio"
                      class="hidden"
                      id="application-openin-tab"
                      value="tab"
                      v-model="app.openIn"
                      name="application-open-in"
                    />
                    <label for="application-openin-tab">{{ $t('OPEN_PAGE_IN_TAB') }}</label>
                  </div>
                </div>
                <div class="field">
                  <div class="ui radio checkbox">
                    <input
                      type="radio"
                      class="hidden"
                      id="application-openin-window"
                      value="window"
                      v-model="app.openIn"
                      name="application-open-in"
                    />
                    <label for="application-openin-window">{{ $t('OPEN_PAGE_IN_WINDOW') }}</label>
                  </div>
                </div>
                <div class="field">
                  <div class="ui radio checkbox">
                    <input
                      type="radio"
                      class="hidden"
                      id="application-openin-incognito"
                      value="incognito"
                      v-model="app.openIn"
                      name="application-open-in"
                    />
                    <label for="application-openin-incognito">
                      {{ $t('OPEN_PAGE_IN_INCOGNITO') }}
                    </label>
                  </div>
                </div>
                <div class="field" v-if="app.type === 'WEB-CHROME' || app.type === 'WEB-EDGE'">
                  <div class="ui radio checkbox">
                    <input
                      type="radio"
                      class="hidden"
                      id="application-openin-app"
                      value="app"
                      v-model="app.openIn"
                      name="application-open-in"
                    />
                    <label for="application-openin-app">{{ $t('OPEN_PAGE_IN_APP') }}</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Launch with arguments -->
            <div
              class="four wide field"
              v-diff-field="appDiff.launchwitharguments"
              v-if="!isLaunchWithArgumentsDisabled"
            >
              <label for="appLaunchWithArguments">{{ $t('LAUNCHWITHARGUMENTS') }}</label>
              <input
                id="appLaunchWithArguments"
                type="text"
                :placeholder="$t('LAUNCHWITHARGUMENTS')"
                :disabled="isLaunchWithArgumentsDisabled || app.isDiff"
                v-model.trim="app.launchwitharguments"
              />
            </div>

            <!-- Working directory -->
            <div
              class="four wide field"
              v-diff-field="appDiff.workingdirectory"
              v-if="!isWorkingDirectoryDisabled"
            >
              <label for="appWorkingDirectory">{{ $t('WORKINGDIRECTORY') }}</label>
              <input
                id="appWorkingDirectory"
                type="text"
                :placeholder="$t('WORKINGDIRECTORY')"
                :disabled="isWorkingDirectoryDisabled || app.isDiff"
                v-model.trim="app.workingdirectory"
              />
            </div>

            <div
              class="two wide field"
              v-diff-field.disable="appDiff.launchtimeout"
              v-if="supportsLaunchTimeout"
            >
              <label for="launchTimeout">{{ $t('LAUNCHTIMEOUT') }}</label>
              <input
                id="launchTimeout"
                type="number"
                :placeholder="$t('LAUNCHTIMEOUT')"
                v-model.trim.number="app.launchtimeout"
              />
            </div>
          </div>

          <!-- Flag: Disable the KnownExecutable functionality -->
          <div
            class="field"
            v-diff-field.disable="appDiff.disableKnownExecutable"
            v-if="supportsKnownExecutable"
          >
            <div class="ui toggle checkbox">
              <input
                id="disableKnownExecutable"
                type="checkbox"
                v-model="app.disableKnownExecutable"
              />
              <label for="disableKnownExecutable">
                {{ $t('DISABLE_KNOWN_EXECUTABLE') }}
              </label>
            </div>
          </div>
        </template>
      </div>

      <template v-if="app.type == 'WEB-HIE' || app.type == 'WEB-HCHROME'">
        <h4 class="ui horizontal left aligned divider header">
          {{ $t('DETACH_SECTION') }}
        </h4>
        <div class="ui basic segment">
          <div class="six wide field" v-diff-field.disable="appDiff.killOnDetach">
            <typeahead
              :items="killOnDetachOptions"
              :current-item="selectedKillOnDetach && selectedKillOnDetach.name"
              :label-text="$t('WINDOW_BEHAVIOR_ON_DETACH')"
              :placeholder-text="$t('WINDOW_BEHAVIOR_ON_DETACH')"
              :empty-text="$t('USE_GLOBAL_SETTING')"
              :select-handler="killOnDetachSelected"
              :remove-handler="killOnDetachRemoved"
            />
          </div>
        </div>
      </template>

      <template
        v-if="
          isNotHeadlessApp &&
            app.type !== 'WEB-HIE' &&
            app.type !== 'WEB-HCHROME' &&
            isManagedApplication
        "
      >
        <h4 class="ui horizontal left aligned divider header">Contains parameters</h4>
        <div class="ui basic segment">
          <div class="four fields" id="containsFields" :class="{ error: areContainsFieldsEmpty }">
            <!-- Titlebar contains -->
            <regex-field
              v-model.trim="app.titlebarcontains"
              :labelText="$t('TITLEBAR_CONTAINS')"
              :disabled="isTitleBarContainsDisabled || app.isDiff"
              :diff="appDiff.titlebarcontains"
              v-if="!isTitleBarContainsDisabled"
              :class="{ error: $v.app.titlebarcontains.$error }"
              @click="$v.app.titlebarcontains.$touch()"
            />

            <!-- Frame contains -->
            <regex-field
              v-model.trim="app.framecontains"
              :labelText="$t('FRAME_CONTAINS')"
              :disabled="isFrameContainsDisabled || app.isDiff"
              :diff="appDiff.framecontains"
              v-if="!isFrameContainsDisabled"
              :class="{ error: $v.app.framecontains.$error }"
              @click="$v.app.framecontains.$touch()"
            />

            <!-- Url contains -->
            <regex-field
              v-model.trim="app.urlcontains"
              :labelText="$t('URL_CONTAINS')"
              :disabled="isUrlContainsDisabled || app.isDiff"
              :diff="appDiff.urlcontains"
              v-if="!isUrlContainsDisabled"
              :class="{ error: $v.app.urlcontains.$error }"
              @click="$v.app.urlcontains.$touch()"
            />

            <!-- Process name contains -->
            <regex-field
              v-model.trim="app.processcontains"
              :labelText="$t('PROCESS_CONTAINS')"
              :diff="appDiff.processcontains"
              v-if="!isProcessNameContainsDisabled"
              :class="{ error: $v.app.processcontains.$error }"
              @click="$v.app.processcontains.$touch()"
            />

            <!-- Process executable path -->
            <regex-field
              v-model.trim="app.executablepathcontains"
              :labelText="$t('EXECUTABLE_PATH_CONTAINS')"
              :diff="appDiff.executablepathcontains"
              v-if="!isExecutablesPathContainsDisabled"
              :class="{ error: $v.app.executablepathcontains.$error }"
              @click="$v.app.executablepathcontains.$touch()"
            />
          </div>
        </div>
      </template>
    </div>

    <loading-button
      slot="rightBottomMenuSlot1"
      class="ui green icon button"
      :onClick="downloadJSON"
      v-if="isEditMode"
      :disabled="isOutdated || app.isDiff"
    >
      <i class="download icon"></i>
    </loading-button>
    <a
      slot="rightBottomMenuSlot1"
      ref="downloadLink"
      href="#"
      style="display: none;"
      v-if="isEditMode"
    />

    <loading-button
      slot="rightBottomMenuSlot2"
      class="ui labeled icon gray button"
      :onClick="copyApp"
      v-if="isEditMode"
      :disabled="isOutdated || app.isDiff"
    >
      {{ $t('COPY', { thing: '' }) }}
      <i class="copy icon"></i>
    </loading-button>

    <button
      slot="rightBottomCreateMenuSlot"
      type="button"
      class="ui green icon button"
      @click="uploadJSON()"
      :disabled="isOutdated"
    >
      <i class="upload icon"></i>
    </button>
    <input
      slot="rightBottomCreateMenuSlot"
      ref="uploadButton"
      type="file"
      accept=".json"
      style="display: none;"
    />
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
.radiobutton-list-description {
  margin-left: 3.9em;
}
.appTypeName {
  margin-left: 0.4em;
}

#applicationSettings {
  overflow-y: scroll;
}

#typeMessage {
  height: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
}

#typeChooser {
  i {
    margin-left: 5px;
    margin-right: 12px;
  }
}
</style>

<script>
// @flow
import _ from 'lodash'
import Vue from 'vue'
import {
  Auth,
  Application,
  Batch,
  Group,
  Manufacturer,
  Field,
  File,
  Flow,
  Http,
  Toast,
  Validation,
  Typenames,
  ErrorCodes
} from 'services'
import { RegexField, LoadingButton, MainSection, Typeahead, GroupTypeahead } from 'ui'
import { catchError, take } from 'rxjs/operators'
import { required } from 'vuelidate/lib/validators'
import ApplicationMenu from './ApplicationMenu'
import { ActionTypes } from 'store'
import GroupAffiliated from '../Mixins/GroupAffiliated'

export default {
  name: 'app-settings',
  mixins: [GroupAffiliated],

  components: {
    ApplicationMenu,
    Typeahead,
    LoadingButton,
    MainSection,
    RegexField,
    GroupTypeahead
  },

  props: {
    initApp: {},
    mode: {
      type: String,
      default: () => ''
    },
    copied: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      app: this.initApp,
      appTypes: [],
      isLoading: false,
      isOutdated: false,
      appDiff: {},
      tempType: null,
      killOnDetachOptions: [
        { id: true, name: this.$i18n.t('CLOSE_WINDOW_ON_DETACH') },
        { id: false, name: this.$i18n.t('LET_WINDOW_REMAIN_OPEN_ON_DETACH') }
      ],
      showLaunchWithFlow: !_.isEmpty(this.app?.launchWithFlowId)
    }
  },
  validations: {
    app: {
      name: {
        required
      },
      titlebarcontains: {
        containsEmptyValidator: Validation.appContainsEmptyValidator
      },
      framecontains: {
        containsEmptyValidator: Validation.appContainsEmptyValidator
      },
      urlcontains: {
        containsEmptyValidator: Validation.appContainsEmptyValidator
      },
      processcontains: {
        containsEmptyValidator: Validation.appContainsEmptyValidator
      },
      executablepathcontains: {
        containsEmptyValidator: Validation.appContainsEmptyValidator
      }
    },
    containsGroup: [
      'app.titlebarcontains',
      'app.framecontains',
      'app.urlcontains',
      'app.processcontains',
      'app.executablepathcontains'
    ]
  },
  computed: {
    launchWithFlowName() {
      return _.find(this.flowsOnApp, flow => flow.id === this.app.launchWithFlowId)?.name
    },
    flowsOnApp() {
      return _.filter(this.$store.state.flow.items, flow => flow.application === this.app.id)
    },
    selectedKillOnDetach() {
      return _.find(this.killOnDetachOptions, o => o.id === this.app.killOnDetach)
    },
    appType() {
      return this.appTypeByName[this.tempType || (this.app && this.app.type)]
    },
    appTypeByName() {
      return this.appTypes ? _.keyBy(this.appTypes, 'type') : {}
    },
    selectableAppTypes() {
      return this.appTypes.filter(t => t.id !== 'WEB-IE-PROXY')
    },
    manufacturers() {
      return this.$store.state.manufacturer.items
    },
    isAppEdited() {
      return this.areGroupsEdited
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    savedGroups() {
      return _.filter(this.groups, g => _.includes(g.application, this.app.id))
    },
    appManufacturer() {
      return _.find(this.manufacturers, { id: this.app.manufacturer }) || {}
    },
    isWorkingDirectoryDisabled() {
      return Validation.isWorkingDirectoryDisabled(this.app)
    },
    isLaunchWithArgumentsDisabled() {
      return Validation.isLaunchWithArgumentsDisabled(this.app)
    },
    isTitleBarContainsDisabled() {
      return Validation.isTitleBarContainsDisabled(this.app)
    },
    isFrameContainsDisabled() {
      return Validation.isFrameContainsDisabled(this.app)
    },
    isUrlContainsDisabled() {
      return Validation.isUrlContainsDisabled(this.app)
    },
    isProcessNameContainsDisabled() {
      return Validation.isProcessNameContainsDisabled(this.app)
    },
    isExecutablesPathContainsDisabled() {
      return Validation.isExecutablesPathContainsDisabled(this.app)
    },
    isManagedApplication() {
      return Validation.isManagedApplication(this.app)
    },
    isTabbedBrowserApplication() {
      return Validation.isTabbedBrowserApplication(this.app)
    },
    isNotHeadlessApp() {
      return this.app.type !== 'HEADLESS'
    },
    supportsLaunchTimeout() {
      return (
        this.app.type === 'NATIVE' ||
        this.app.type === 'JAVA-AWT' ||
        this.app.type === 'CONTEXTPARTICIPANT'
      )
    },
    supportsKnownExecutable() {
      return this.app.type === 'NATIVE'
    },
    areContainsFieldsEmpty() {
      return this.$v.containsGroup
        ? Object.keys(this.$v.containsGroup)
            .filter(key => key.startsWith('app') && this.$v.containsGroup[key])
            .reduce(
              (flag, key) =>
                flag ||
                (this.$v.containsGroup[key].$dirty &&
                  !this.$v.containsGroup[key].containsEmptyValidator),
              false
            )
        : false
    },
    allowHChromeMedia: {
      get() {
        return this.app.switches && this.app.switches.indexOf('enable-media-stream=1') >= 0
      },
      set(allow) {
        const curSwitches = (this.app.switches && this.app.switches.split(',')) || []
        this.$set(
          this.app,
          'switches',
          allow
            ? _.sortBy(_.uniq([...curSwitches, 'enable-media-stream=1'])).join(',')
            : _.without(curSwitches, 'enable-media-stream=1').join(',')
        )
      }
    }
  },

  methods: {
    selectLaunchWithFlow(flow) {
      this.$set(this.app, 'launchWithFlowId', flow && flow.id ? flow.id : null)
    },
    killOnDetachSelected(option) {
      this.app.killOnDetach = option.id
    },
    killOnDetachRemoved() {
      this.app.killOnDetach = null
    },
    updateTemporaryTypeDescription(item) {
      this.tempType = item ? item.type : null
    },
    appTypeSelected(item) {
      this.app.type = item.type
      this.tempType = null
    },

    generateJSON() {
      return Promise.all([
        Field.readFieldsForApp(this.app.id),
        Flow.readFlowsForApp(this.app.id)
      ]).then(([fields, flows]) => {
        flows.forEach(f => delete f.maintainer)
        return JSON.stringify({
          application: this.app,
          manufacturer: this.appManufacturer,
          fields: fields,
          flows: flows
        })
      })
    },

    downloadJSON() {
      return this.generateJSON().then(json => {
        File.downloadFile(
          json,
          this.app.name + '.json',
          'application/json',
          this.$refs.downloadLink
        )
      })
    },

    readApp(appId) {
      return Application.readApp(appId)
        .then(app => {
          this.app = app
          this.resetGroupChanges()
          this.isOutdated = false
          this.app.openIn = this.app.openIn || 'tab'
          return app
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('APPLICATION', 1) })
            })
          )
          console.error(err)
        })
    },

    updateApp() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Application.updateApp(this.app.id, this.app)
            .then(res => {
              this.app.version = res.version
              return this.saveGroups('application', this.app.id)
            })
            .then(() => {
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('APPLICATION', 1) }))
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', {
                      thing: this.$i18n.tc('APPLICATION', 1)
                    }),
                    thing: this.$i18n.tc('APPLICATION', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('APPLICATION', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('APPLICATION', 1) }),
              thing: this.$i18n.tc('APPLICATION', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('APPLICATION', 1) })
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteApp() {
      Promise.all([Flow.readFlowsForApp(this.app.id), Field.readFieldsForApp(this.app.id)])
        .then(([appFlows, appFields]) =>
          Batch.deleteAppBatch(this.app.id, appFlows.map(f => f.id), appFields.map(f => f.id))
        )
        .catch(Http.handleError)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('APPLICATION', 1) }))
          this.$router.push({ name: 'applications' })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('APPLICATION', 1) })
            }),
            { text: this.$i18n.t('TRY_AGAIN') }
          )
          console.error(err)
        })
    },

    copyApp() {
      if (!confirm(this.$t('COPY_CONFIRM', { thing: this.app.name }))) return
      let userName = Auth.getLoggedInUser()
      return Promise.all([Field.readFieldsForApp(this.app.id), Flow.readFlowsForApp(this.app.id)])
        .then(([fields, flows]) => {
          flows.forEach(f => delete f.id)
          fields.forEach(f => delete f.id)
          const copiedApp = {
            ...this.app,
            name: `${this.app.name} (${this.$t('COPIED_BY', {
              name: userName || this.$t('UNKNOWN')
            })})`
          }
          delete copiedApp.id
          return {
            application: copiedApp,
            manufacturer: this.appManufacturer,
            fields: fields,
            flows: flows
          }
        })
        .then(batchObj => this.createUploadedAppFromJson(batchObj, true))
        .catch(Http.handleError)
    },

    createApp() {
      if (!this.$v.$invalid) {
        return Application.createApp(this.app)
          .then(res => {
            this.app.id = res.id
            this.app.version = res.version
            return Promise.all(
              this.editedGroups.map(group =>
                Group.addAffiliate('application', group.id, this.app.id)
              )
            )
          })
          .then(() => {
            this.resetGroupChanges()
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('APPLICATION', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'app-settings',
              params: { appId: this.app.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('APPLICATION', 1) })
              }),
              { remove: 5000 }
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('APPLICATION', 1) })
          })
        )
      }
    },

    createManufacturer(manufacturer) {
      return Manufacturer.createManufacturer(manufacturer)
        .then(res => {
          Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('MANUFACTURER', 1) }), {
            remove: 5000
          })
          return res.id
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('MANUFACTURER', 1) })
            })
          )
          console.error(err)
        })
    },

    manufacturerSelected(manufacturer) {
      this.app.manufacturer = manufacturer.id
    },

    manufacturerRemoved() {
      this.app.manufacturer = ''
    },

    manufacturerCreated(manufacturerName) {
      return this.createManufacturer({ name: manufacturerName }).then(manufacturerId => {
        this.app.manufacturer = manufacturerId

        return {
          id: manufacturerId,
          name: manufacturerName
        }
      })
    },
    uploadJSON() {
      this.$refs.uploadButton.click()
    },

    processFile(file) {
      if (file.size > 10000000) {
        Toast.error(this.$i18n.t('ERR_FILE_TOO_LARGE', { max: '10MB' }))
      } else {
        this.isLoading = true
        let reader = new FileReader()
        reader.onload = e => {
          const json = JSON.parse(e.target.result)
          if (this.isJsonValid(json)) {
            Application.readApp(json.application.id)
              .then(app => {
                if (this.appNotExist(app) || this.shouldOverrideApp()) {
                  this.createUploadedAppFromJson(json)
                } else {
                  this.isLoading = false
                }
              })
              .catch(err => {
                console.error(err)
                this.isLoading = false
                Toast.error(
                  this.$i18n.t('IMPORT_FROM_FILE_ERROR', { thing: this.$i18n.tc('APPLICATION', 1) })
                )
              })
          } else {
            this.isLoading = false
            Toast.warning(this.$i18n.t('INVALID_FILE'))
          }
        }
        reader.onerror = e => {
          console.error('Reader error', e)
          this.isLoading = false
        }
        reader.readAsText(file)
      }
    },
    isJsonValid(json) {
      return (
        Application.validateAppObject(json.application) &&
        Validation.validateFlows(json.flows) &&
        Validation.validateFields(json.fields)
      )
    },
    appNotExist(app) {
      return _.isEmpty(app)
    },
    shouldOverrideApp() {
      return window.confirm(this.$i18n.t('APPLICATION_ALREADY_EXISTS_WARN'))
    },
    createUploadedAppFromJson(json, isCopy) {
      return Batch.createUploadedAppBatch(json, isCopy)
        .then(appId => {
          this.isLoading = false
          Toast.success(
            this.$i18n.t(isCopy ? 'COPIED' : 'CREATED', { thing: this.$i18n.tc('APPLICATION', 1) }),
            {
              remove: 5000
            }
          )
          this.$router.push({
            name: 'app-settings',
            params: { appId: appId, modeArg: isCopy ? 'copy' : '' }
          })
        })
        .catch(err => {
          this.isLoading = false
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t(isCopy ? 'COPIED' : 'CREATED', {
                thing: this.$i18n.tc('APPLICATION', 1)
              })
            }),
            { remove: 5000 }
          )
          console.error(err)
        })
    },
    dropFunction(e) {
      if (
        e.dataTransfer.files.length === 1 &&
        e.dataTransfer.files[0].type === 'application/json'
      ) {
        const file = e.dataTransfer.files[0]
        this.processFile(file)
      }
    },
    updateEventListeners() {
      if (this.mode === 'create') {
        File.addDropFileFunctionalityToDroptarget(this.$refs.droptarget, this.dropFunction)

        this.$refs.uploadButton.addEventListener('change', e => {
          this.processFile(e.target.files[0])
        })
      } else {
        if (this.$refs.droptarget)
          this.$refs.droptarget.removeEventListener('drop', this.dropFunction)
      }
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.app = changedItem
      this.appDiff = fullDiff
    },
    onRevert(app) {
      this.app = app
      this.appDiff = {}
    }
  },
  created() {
    Manufacturer.readAllManufacturers()
    Application.getAllApplicationTypes().then(types => {
      this.appTypes = _.map(types, t => ({
        id: t.type,
        name: t.name || t.type,
        ...t
      }))
    })
    Flow.readAllFlows()
  },
  mounted() {
    this.updateEventListeners()
  },
  updated() {
    this.updateEventListeners()
  },
  watch: {
    initApp: function(newApp) {
      if (!_.isEmpty(newApp)) {
        this.$v.$reset()
        this.isOutdated = false
        this.app = newApp
        this.app.openIn = this.app.openIn || 'tab'
        this.$nextTick(() => {
          if (this.copied) this.$refs.nameInput.focus()
        })
      }
    },
    mode: function() {
      this.$v.$reset()
    },
    app: function(newApp) {
      this.showLaunchWithFlow = !_.isEmpty(newApp?.launchWithFlowId)
    },
    'app.launchwith': function(newLaunchWith) {
      if (newLaunchWith) this.app.launchWithFlowId = null
    },
    'app.launchWithFlowId': function(newLaunchWithFlowId) {
      if (newLaunchWithFlowId) this.app.launchwith = null
    }
  }
}
</script>
